/**
 * Barra de metas del carrito (reemplaza a la barra de custom goals del
 * carrito Vue). Config: "Metas del carrito" en la configuración del tema.
 *
 * Metas 1-3 se cumplen por monto ($) y 4-6 por unidades. Cada una puede ser:
 *  - custom: solo se muestra (el descuento, envío gratis o cuotas los
 *    aplica Shopify por su lado).
 *  - gift:   agrega el regalo al carrito al cumplirse y lo saca si deja de
 *    cumplirse.
 *
 * Se dibuja una barra por tipo de condición: unidades arriba y monto abajo.
 * Si "Descuento escalonado" está activo con "Carrito" tildado, sus niveles se
 * suman a la barra de unidades.
 */
(() => {
  const CFG = window.TCL_CART_GOALS;
  const watch = window.TCLCartWatch;
  if (!CFG || !watch || customElements.get('cart-goals')) return;

  // Fechas en formato "YYYY-MM-DD HH:MM", en la zona horaria de la tienda.
  const shopOffset = (window.TCL_SHOP_TZ || '-0300').replace(/(\d{2})(\d{2})$/, '$1:$2');
  const parseDate = (value) => {
    if (!value) return null;
    const date = new Date(`${value.trim().replace(' ', 'T')}:00${shopOffset}`);
    return isNaN(date) ? null : date;
  };
  const inDates = (goal) => {
    const now = new Date();
    const start = parseDate(goal.start);
    const end = parseDate(goal.end);
    return (!start || now >= start) && (!end || now <= end);
  };

  const goals = (CFG.goals || []).filter((g) => g.enabled && g.threshold > 0);

  const escapeHtml = (str) =>
    String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const money = (cents) => {
    if (window.$Cart?.convertCurrency) return window.$Cart.convertCurrency(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'es', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'ARS',
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  // "###" (formato de custom goals) o "[faltan]" se reemplazan por lo que falta.
  const fillMessage = (template, missing) =>
    escapeHtml(template).replace(/###|\[faltan\]/g, `<strong class="td__hl">${escapeHtml(missing)}</strong>`).replace(
      /\[productos\]/g,
      missing === '1' ? 'producto' : 'productos'
    );

  // --- Armado de las barras -------------------------------------------------

  const unitGoals = () => {
    const list = goals.filter((g) => g.condition === 'units' && inDates(g));
    const tier = window.TCLTierDiscount;
    if (tier?.showInCart && tier.isActive()) {
      tier.tiers.forEach((t) => list.push({ threshold: t.qty, label: `${t.pct}%`, fromTier: true }));
    }
    return list.sort((a, b) => a.threshold - b.threshold);
  };

  const amountGoals = () =>
    goals
      .filter((g) => g.condition !== 'units' && inDates(g))
      .map((g) => ({ ...g, cents: g.threshold * 100 }))
      .sort((a, b) => a.cents - b.cents);

  const buildTracks = (cart) => {
    const tracks = [];

    const units = unitGoals();
    if (units.length) {
      const value = watch.units(cart);
      const next = units.find((g) => value < g.threshold);
      const nextTier = units.find((g) => g.fromTier && value < g.threshold);
      const last = units[units.length - 1];
      let headline;
      // Si una meta propia coincide con un nivel del escalonado, manda el escalonado.
      if (nextTier && nextTier.threshold === next.threshold) headline = window.TCLTierDiscount.headline(value);
      else if ((next || last).fromTier) headline = window.TCLTierDiscount.headline(value);
      else if (next) headline = fillMessage(next.pending, String(next.threshold - value));
      else headline = escapeHtml(last.reached);
      tracks.push({
        kind: 'units',
        value,
        headline,
        steps: units.map((g, i) => ({
          label: g.label,
          target: g.threshold,
          caption: `${g.threshold}${i === units.length - 1 ? '+' : ''} prod.`,
          hint: (v) => (v >= g.threshold ? '&#10003;' : `+${g.threshold - v}`),
        })),
      });
    }

    const amounts = amountGoals();
    if (amounts.length) {
      const value = watch.amount(cart);
      const next = amounts.find((g) => value < g.cents);
      const last = amounts[amounts.length - 1];
      tracks.push({
        kind: 'amount',
        value,
        headline: next ? fillMessage(next.pending, money(next.cents - value)) : escapeHtml(last.reached),
        steps: amounts.map((g) => ({
          label: g.label,
          target: g.cents,
          caption: money(g.cents),
          hint: (v) => (v >= g.cents ? '&#10003;' : ''),
        })),
      });
    }

    return tracks;
  };

  const stepsMarkup = (track) =>
    track.steps
      .map(
        (step) => `
          <li class="td__step">
            <span class="td__pct${step.label.length > 5 ? ' td__pct--text' : ''}">${escapeHtml(step.label)}</span>
            <span class="td__bar"><span class="td__fill"></span></span>
            <span class="td__qty"><span class="td__qty-label">${escapeHtml(step.caption)}</span><span class="td__hint"></span></span>
          </li>`
      )
      .join('');

  // Cada meta es un tramo de la barra, que se llena desde la meta anterior.
  const updateSteps = (root, track) => {
    root.querySelectorAll('.td__step').forEach((el, i) => {
      const step = track.steps[i];
      const prev = i === 0 ? 0 : track.steps[i - 1].target;
      const reached = track.value >= step.target;
      el.classList.toggle('is-reached', reached);
      el.classList.toggle('is-next', !reached && track.value >= prev);
      el.classList.toggle(
        'is-current',
        reached && (i === track.steps.length - 1 || track.value < track.steps[i + 1].target)
      );
      el.querySelector('.td__fill').style.setProperty(
        '--td-fill',
        Math.min(Math.max((track.value - prev) / (step.target - prev), 0), 1)
      );
      el.querySelector('.td__hint').innerHTML = step.hint(track.value);
    });
  };

  const reachedCount = (track) => track.steps.filter((s) => track.value >= s.target).length;

  class CartGoals extends HTMLElement {
    connectedCallback() {
      this.unsubscribe = watch.subscribe((cart) => this.update(cart));
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    update(cart) {
      const tracks = buildTracks(cart);
      this.hidden = !tracks.length;
      if (!tracks.length) return;

      const key = tracks.map((t) => `${t.kind}:${t.steps.map((s) => `${s.label}@${s.target}`).join(',')}`).join('|');
      if (key !== this._key) {
        this._key = key;
        this._reached = null;
        this.innerHTML = tracks
          .map(
            (t) =>
              `<div class="cg__track" data-kind="${t.kind}"><p class="td__headline" aria-live="polite"></p><ol class="td__steps">${stepsMarkup(t)}</ol></div>`
          )
          .join('');
      }

      const trackEls = this.querySelectorAll('.cg__track');
      tracks.forEach((track, i) => {
        trackEls[i].querySelector('.td__headline').innerHTML = track.headline;
        // Doble frame: la primera vez la barra arranca vacía y se llena animada.
        requestAnimationFrame(() => requestAnimationFrame(() => updateSteps(trackEls[i], track)));
      });

      const reached = tracks.map(reachedCount);
      if (this._reached && reached.some((n, i) => n > this._reached[i])) this.celebrate();
      this._reached = reached;
      this.classList.toggle('is-max', tracks.every((t) => reachedCount(t) === t.steps.length));
    }

    celebrate() {
      this.classList.remove('td--celebrate');
      void this.offsetWidth;
      this.classList.add('td--celebrate');
      clearTimeout(this._celebrateTimer);
      this._celebrateTimer = setTimeout(() => this.classList.remove('td--celebrate'), 1200);
    }
  }

  customElements.define('cart-goals', CartGoals);

  // --- Qué meta desbloquea sumar un producto --------------------------------
  // Para marcar recomendaciones ("Sumalo: 20% OFF") y la línea de la PDP.

  const gainFor = (cart, cents) => {
    const units = watch.units(cart);
    const unitGain = unitGoals().find((g) => units < g.threshold && units + 1 >= g.threshold);
    if (unitGain) return unitGain.fromTier ? `${unitGain.label} OFF` : unitGain.label;

    const amount = watch.amount(cart);
    const amountGain = amountGoals()
      .reverse()
      .find((g) => amount < g.cents && amount + cents >= g.cents);
    return amountGain ? amountGain.label : null;
  };

  window.TCLCartGoals = { gainFor };

  // --- Línea de metas por monto en la PDP -----------------------------------
  // "Te faltan $X para envío gratis" / "Con este producto llegás a ENVÍO GRATIS".
  // data-prices: { variantId: precio en centavos }; data-form: id del form.

  class CartGoalLine extends HTMLElement {
    connectedCallback() {
      try {
        this.prices = JSON.parse(this.dataset.prices || '{}');
      } catch (e) {
        this.prices = {};
      }
      this.unsubscribe = watch.subscribe((cart) => this.update(cart));
      // Cambio de variante en la PDP.
      this.onChange = () => watch.cart && this.update(watch.cart);
      document.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      document.removeEventListener('change', this.onChange);
    }

    variantPrice() {
      const input = document.querySelector(`#${this.dataset.form} [name="id"]`);
      return this.prices[input?.value] ?? Object.values(this.prices)[0] ?? 0;
    }

    update(cart) {
      const goals = amountGoals();
      this.hidden = !goals.length;
      if (!goals.length) return;

      const amount = watch.amount(cart);
      const next = goals.find((g) => amount < g.cents);
      let text;
      if (!next) text = `&#10003; ${escapeHtml(goals[goals.length - 1].reached)}`;
      else if (amount + this.variantPrice() >= next.cents)
        text = `Con este producto llegás a <strong class="td__hl">${escapeHtml(next.label)}</strong>`;
      else text = fillMessage(next.pending, money(next.cents - amount));

      this.innerHTML = `
        <p class="goal-line__text">${text}</p>
        <span class="goal-line__bar"><span class="goal-line__fill" style="--td-fill:${next ? Math.min(amount / next.cents, 1) : 1}"></span></span>`;
      this.classList.toggle('is-done', !next);
    }
  }

  customElements.define('cart-goal-line', CartGoalLine);

  // --- Regalos automáticos --------------------------------------------------
  // Las líneas de regalo llevan _isGift (el carrito Vue las muestra como
  // regalo) y _goalGift con el número de meta, para saber cuáles son nuestras.

  const giftGoals = goals.filter((g) => g.action === 'gift' && g.giftVariant);
  const failedVariants = new Set();
  let syncing = false;
  let syncAgain = false;

  const goalMet = (goal, cart) =>
    inDates(goal) &&
    (goal.condition === 'units' ? watch.units(cart) >= goal.threshold : watch.amount(cart) >= goal.threshold * 100);

  const cartRequest = (url, body) =>
    watch.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  const syncGifts = async (cart) => {
    if (syncing) {
      syncAgain = true;
      return;
    }
    syncing = true;
    let changed = false;

    try {
      // Sacar regalos cuya meta ya no se cumple (o que ya no existe).
      for (const line of cart.items.filter((item) => item.properties?._goalGift)) {
        const goal = giftGoals.find((g) => String(g.index) === String(line.properties._goalGift));
        if (!goal || goal.giftVariant !== line.variant_id || !goalMet(goal, cart)) {
          await cartRequest('/cart/change.js', { id: line.key, quantity: 0 });
          changed = true;
        }
      }

      // Agregar regalos de metas cumplidas.
      for (const goal of giftGoals) {
        if (failedVariants.has(goal.giftVariant) || !goalMet(goal, cart)) continue;
        const inCart = cart.items.some(
          (item) => item.variant_id === goal.giftVariant && String(item.properties?._goalGift) === String(goal.index)
        );
        if (inCart) continue;
        const res = await cartRequest('/cart/add.js', {
          items: [{ id: goal.giftVariant, quantity: 1, properties: { _isGift: true, _goalGift: String(goal.index) } }],
        });
        // Sin stock u otro error: no se reintenta en esta visita.
        if (!res.ok) failedVariants.add(goal.giftVariant);
        else changed = true;
      }
    } catch (e) {
      console.error('[cart-goals]', e);
    } finally {
      syncing = false;
      if (changed) {
        if (typeof window.$Cart?.get === 'function') window.$Cart.get();
        watch.refresh();
      } else if (syncAgain) {
        syncAgain = false;
        watch.refresh();
      }
    }
  };

  if (giftGoals.length) watch.subscribe(syncGifts);

  // --- Montaje dentro del carrito Vue --------------------------------------
  // `.cart--notices` es un nodo estático del template: Vue no lo vuelve a
  // parchear, así que lo que insertamos ahí sobrevive a los re-renders.
  // Algunas tiendas (Chile) tienen otra versión del bundle sin ese nodo: ahí
  // va justo debajo del encabezado del carrito.

  let goalsEl;
  const mountInCart = () => {
    const root = document.getElementById('ModalCart');
    if (!root) return;
    if (!goalsEl) {
      goalsEl = document.createElement('cart-goals');
      goalsEl.className = 'td td--cart';
      goalsEl.hidden = true;
    }
    const notices = root.querySelector('.cart--notices');
    if (notices) {
      if (goalsEl.parentNode !== notices) notices.prepend(goalsEl);
      return;
    }
    const header = root.querySelector('.cart--header');
    if (header && header.nextElementSibling !== goalsEl) header.after(goalsEl);
  };

  const watchCart = () => {
    const root = document.getElementById('ModalCart');
    if (!root) return;
    mountInCart();
    new MutationObserver(mountInCart).observe(root, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCart);
  else watchCart();
})();
